import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Template Sync Context
 *
 * Provides a way for Service Music Templates page to notify Planning Page
 * when templates are updated, so the Planning Page can refresh its data.
 */

interface TemplateSyncContextType {
  // Timestamp of last template update
  lastTemplateUpdate: number | null;

  // Call this after saving/updating a template
  notifyTemplateUpdate: () => void;
}

const TemplateSyncContext = createContext<TemplateSyncContextType | undefined>(undefined);

interface TemplateSyncProviderProps {
  children: ReactNode;
}

export const TemplateSyncProvider: React.FC<TemplateSyncProviderProps> = ({ children }) => {
  const [lastTemplateUpdate, setLastTemplateUpdate] = useState<number | null>(null);

  const notifyTemplateUpdate = useCallback(() => {
    setLastTemplateUpdate(Date.now());
    console.log('🔄 Template update notification sent:', new Date().toLocaleTimeString());
  }, []);

  return (
    <TemplateSyncContext.Provider value={{ lastTemplateUpdate, notifyTemplateUpdate }}>
      {children}
    </TemplateSyncContext.Provider>
  );
};

/**
 * Hook to access template sync context
 * @throws Error if used outside of TemplateSyncProvider
 */
export const useTemplateSync = (): TemplateSyncContextType => {
  const context = useContext(TemplateSyncContext);
  if (!context) {
    throw new Error('useTemplateSync must be used within a TemplateSyncProvider');
  }
  return context;
};
